/**
 * MUIBoxTable
 *
 * Lightweight generic table built on MUI Table components.
 * - Accepts typed columns with optional render(row) for cell customization.
 * - Shows ellipsis with tooltip for long text values via CustomTooltip.
 * - Special-cases a column with key "remove" to render a clickable remove icon
 *   that does not trigger the row click handler.
 *
 * Props
 * - columns: { key, label, render? }[]
 * - data: any[]
 * - noResultText?: fallback message when no rows
 * - onRowClick?: optional row click handler
 */
import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./mui-box-table.css";

import { MinusCircleIcon } from "lucide-react";

const UNSORTABLE_COLUMNS = new Set(["edit", "remove"]);

const MUIBoxTable = ({
  columns,
  data,
  noResultText,
  onRowClick,
  tableHeight,
}) => {
  const [sortConfig, setSortConfig] = useState(null);

  const toggleSort = (columnKey, direction) => {
    setSortConfig((prev) => {
      if (prev?.key === columnKey && prev.direction === direction) {
        return null;
      }
      return { key: columnKey, direction };
    });
  };

  const numericPattern = React.useMemo(() => /^-?\d+(?:\.\d+)?$/, []);

  // Helper: Extract comparable value from string
  const extractStringValue = (value) => {
    const trimmed = value.trim();
    return trimmed === "" ? null : trimmed;
  };

  // Helper: Extract comparable value from array
  const extractArrayValue = (value, extractComparableValueFn) => {
    const parts = value
      .map((item) => extractComparableValueFn(item))
      .filter((part) => part !== null);

    if (parts.length === 0) {
      return null;
    }

    const allNumbers = parts.every((part) => typeof part === "number");
    if (allNumbers) {
      return parts[0];
    }

    return parts.map(String).join(", ");
  };

  // Helper: Extract comparable value from React element
  const extractElementValue = (value, extractComparableValueFn) => {
    const props = value.props || {};
    const explicitSortValue =
      // eslint-disable-next-line react/prop-types
      props["data-sort-value"] ?? props["dataSortValue"] ?? props["sortValue"];

    if (
      typeof explicitSortValue === "string" ||
      typeof explicitSortValue === "number"
    ) {
      return explicitSortValue;
    }

    if (Object.hasOwn(props, "children")) {
      // eslint-disable-next-line react/prop-types
      return extractComparableValueFn(props["children"]);
    }

    return null;
  };

  // Helper: Extract comparable value from object
  const extractObjectValue = (value, extractComparableValueFn) => {
    const candidateKeys = [
      "sortValue",
      "label",
      "value",
      "name",
      "title",
      "text",
    ];
    for (const key of candidateKeys) {
      if (!Object.hasOwn(value, key)) continue;
      const candidate = value[key];
      const extracted = extractComparableValueFn(candidate);
      if (extracted !== null) {
        return extracted;
      }
    }
    return null;
  };

  const extractComparableValue = React.useCallback((value) => {
    if (value == null) {
      return null;
    }

    if (typeof value === "number") {
      return value;
    }

    if (typeof value === "string") {
      return extractStringValue(value);
    }

    if (Array.isArray(value)) {
      return extractArrayValue(value, extractComparableValue);
    }

    if (React.isValidElement(value)) {
      return extractElementValue(value, extractComparableValue);
    }

    if (typeof value === "object") {
      return extractObjectValue(value, extractComparableValue);
    }

    return null;
  }, []);

  const getSortValue = React.useCallback(
    (row, columnKey) => {
      const column = columns.find((col) => col.key === columnKey);

      if (column?.render) {
        const rendered = column.render(row);
        const extractedFromRender = extractComparableValue(rendered);
        if (extractedFromRender !== null) {
          return extractedFromRender;
        }
      }

      const raw = row[columnKey];
      const extractedFromRaw = extractComparableValue(raw);
      if (extractedFromRaw !== null) {
        return extractedFromRaw;
      }

      return null;
    },
    [columns, extractComparableValue],
  );

  // Helper: Check if value is null
  const isNullValue = (value) => value == null;

  // Helper: Check if value is numeric
  const isNumericValue = (value, pattern) => {
    return (
      typeof value === "number" ||
      (typeof value === "string" && pattern.test(value))
    );
  };

  // Helper: Compare numeric values
  const compareNumericValues = (aValue, bValue, direction) => {
    const aNumber = Number(aValue);
    const bNumber = Number(bValue);
    if (direction === "asc") {
      return aNumber - bNumber;
    }
    return bNumber - aNumber;
  };

  // Helper: Compare string values
  const compareStringValues = (aValue, bValue, direction) => {
    const aString = String(aValue).toLowerCase();
    const bString = String(bValue).toLowerCase();

    if (direction === "asc") {
      if (aString < bString) return -1;
      if (aString > bString) return 1;
      return 0;
    }

    if (aString > bString) return -1;
    if (aString < bString) return 1;
    return 0;
  };

  // Helper: Compare two values for sorting
  const compareSortValues = (aValue, bValue, direction, pattern) => {
    if (isNullValue(aValue) && isNullValue(bValue)) return 0;
    if (isNullValue(aValue)) return 1;
    if (isNullValue(bValue)) return -1;

    const isANumeric = isNumericValue(aValue, pattern);
    const isBNumeric = isNumericValue(bValue, pattern);

    if (isANumeric && isBNumeric) {
      return compareNumericValues(aValue, bValue, direction);
    }

    return compareStringValues(aValue, bValue, direction);
  };

  // Sort data based on sortConfig
  const sortedData = React.useMemo(
    () => {
      if (!sortConfig) return data;

      return [...data].sort((a, b) => {
        const aValue = getSortValue(a, sortConfig.key);
        const bValue = getSortValue(b, sortConfig.key);
        return compareSortValues(
          aValue,
          bValue,
          sortConfig.direction,
          numericPattern,
        );
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, getSortValue, sortConfig, numericPattern],
  );

  return (
    <TableContainer
      component={Paper}
      className="box-table-container"
      sx={
        tableHeight
          ? { maxHeight: tableHeight, height: tableHeight, overflow: "auto" }
          : undefined
      }
    >
      <Table className="box-table">
        <TableHead>
          <TableRow>
            {columns.map((column) => {
              const isSortable = !UNSORTABLE_COLUMNS.has(column.key);
              let ariaSortValue;
              if (sortConfig?.key === column.key) {
                ariaSortValue =
                  sortConfig.direction === "asc" ? "ascending" : "descending";
              } else {
                ariaSortValue = "none";
              }
              return (
                <TableCell
                  key={column.key}
                  className="box-table-cell box-table-head"
                  aria-sort={ariaSortValue}
                >
                  {column.label}
                  {isSortable && (
                    <button
                      type="button"
                      aria-label={`Sort ${column.label}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (sortConfig?.key !== column.key) {
                          toggleSort(column.key, "asc");
                        } else if (sortConfig.direction === "asc") {
                          toggleSort(column.key, "desc");
                        } else if (sortConfig.direction === "desc") {
                          setSortConfig(null);
                        } else {
                          toggleSort(column.key, "asc");
                        }
                      }}
                      style={{
                        display: "inline-flex",
                        flexDirection: "column",
                        marginLeft: 8,
                        lineHeight: 1,
                        cursor: "pointer",
                        border: 0,
                        background: "transparent",
                        padding: 0,
                      }}
                    >
                      <ArrowDropUpIcon
                        aria-hidden
                        focusable={false}
                        sx={{
                          fontSize: 20,
                          mt: "-2px",
                          mb: "-6px",
                          color:
                            sortConfig?.key === column.key &&
                            sortConfig.direction === "asc"
                              ? "#4B3F72"
                              : "#4B3F7280",
                          transition: "color .15s",
                        }}
                      />
                      <ArrowDropDownIcon
                        aria-hidden
                        focusable={false}
                        sx={{
                          fontSize: 20,
                          mt: "-6px",
                          color:
                            sortConfig?.key === column.key &&
                            sortConfig.direction === "desc"
                              ? "#4B3F72"
                              : "#4B3F7280",
                          transition: "color .15s",
                        }}
                      />
                    </button>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedData.length > 0 ? (
            sortedData.map((row, rowIndex) => (
              <TableRow
                key={row.id || rowIndex}
                className={onRowClick ? "clickable-row" : "box-table-body-row"}
                onClick={() => {
                  onRowClick?.(row);
                }}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={(e) => {
                  if (!onRowClick) return;
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onRowClick(row);
                  }
                }}
                aria-label={onRowClick ? "Row action" : undefined}
              >
                {columns.map((column) => {
                  const cellValue = column.render
                    ? column.render(row)
                    : row[column.key];

                  const isText =
                    typeof cellValue === "string" ||
                    typeof cellValue === "number";
                  let renderedCell;

                  if (column.key === "remove") {
                    renderedCell = (
                      <button
                        type="button"
                        aria-label="Remove"
                        className="remove-icon"
                        style={{
                          cursor: "pointer",
                          width: 18,
                          height: 18,
                          background: "none",
                          border: "none",
                          padding: 0,
                        }}
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          // If you have a remove handler, call it here
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            // If you have a remove handler, call it here
                          }
                        }}
                      >
                        <img
                          src={MinusCircleIcon}
                          alt="Remove"
                          style={{ width: 18, height: 18 }}
                          aria-hidden="true"
                        />
                      </button>
                    );
                  } else if (isText) {
                    renderedCell = (
                      <span
                        className="cell-ellipsis"
                        title={String(cellValue)}
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#403C6F",
                        }}
                      >
                        {cellValue}
                      </span>
                    );
                  } else if (
                    (cellValue?.props?.children ||
                      cellValue?.props?.children === 0) &&
                    column.key !== "remove"
                  ) {
                    renderedCell = (
                      <span title={String(cellValue?.props?.children)}>
                        {cellValue ?? "-"}
                      </span>
                    );
                  } else {
                    renderedCell = cellValue ?? "-";
                  }
                  return (
                    <TableCell key={column.key} className="box-table-cell">
                      {renderedCell}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={columns.length}
                className="no-data"
                style={{ textAlign: "center" }}
              >
                {noResultText || "No data found"}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

MUIBoxTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      render: PropTypes.func,
    }),
  ).isRequired,
  data: PropTypes.array.isRequired,
  noResultText: PropTypes.string,
  onRowClick: PropTypes.func,
  tableHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default MUIBoxTable;
